import React, { useState, useEffect } from 'react';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { ThumbsUp, ThumbsDown, Loader2, ChevronLeft, ChevronRight, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL as API } from '@/utils/apiConfig';


export default function AdminFeedbackPage() {
  const { isDark, colors } = useAdminTheme();
  const [feedback, setFeedback] = useState([]);
  const [stats, setStats] = useState({ total_up: 0, total_down: 0 });
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [feedbackVisible, setFeedbackVisible] = useState(false);
  const [toggleLoading, setToggleLoading] = useState(false);

  const fetchFeedback = async (p = 1, ratingFilter = filter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 30 });
      if (ratingFilter !== 'all') params.set('rating', ratingFilter);
      const res = await axios.get(`${API}/admin/feedback?${params}`, { withCredentials: true });
      setFeedback(res.data.feedback);
      setStats({ total_up: res.data.total_up, total_down: res.data.total_down });
      setTotal(res.data.total);
      setPage(p);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const fetchFlags = async () => {
    try {
      const res = await axios.get(`${API}/admin/feature-flags`, { withCredentials: true });
      const list = res.data?.flags || [];
      const flag = list.find((f) => f.key === 'user_feedback_visible');
      setFeedbackVisible(!!flag?.enabled);
    } catch { /* ignore */ }
  };

  const toggleFeedbackVisibility = async () => {
    setToggleLoading(true);
    try {
      await axios.put(`${API}/admin/feature-flags/user_feedback_visible`, { enabled: !feedbackVisible }, { withCredentials: true });
      setFeedbackVisible(!feedbackVisible);
      toast.success(`User feedback ${!feedbackVisible ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update');
    }
    setToggleLoading(false);
  };

  useEffect(() => { fetchFeedback(); fetchFlags(); }, []);

  const totalPages = Math.ceil(total / 30);
  const totalAll = stats.total_up + stats.total_down;
  const satisfaction = totalAll > 0 ? Math.round((stats.total_up / totalAll) * 100) : 0;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-feedback-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>User Feedback</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>AI response quality ratings</p>
          </div>
          <Button
            onClick={toggleFeedbackVisibility}
            disabled={toggleLoading}
            variant="outline"
            size="sm"
            className="gap-2"
            style={{ color: colors.text, borderColor: colors.cardBorder }}
            data-testid="toggle-feedback-visibility"
          >
            {feedbackVisible ? <ToggleRight className="h-4 w-4 text-green-400" /> : <ToggleLeft className="h-4 w-4" />}
            {feedbackVisible ? 'Visible to Users' : 'Hidden from Users'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <StatBox label="Positive" value={stats.total_up} icon={ThumbsUp} color="#10b981" colors={colors} isDark={isDark} />
          <StatBox label="Negative" value={stats.total_down} icon={ThumbsDown} color="#ef4444" colors={colors} isDark={isDark} />
          <StatBox label="Satisfaction" value={`${satisfaction}%`} icon={ThumbsUp} color="#6366f1" colors={colors} isDark={isDark} />
        </div>

        {/* Filter */}
        <div className="flex gap-2">
          {['all', 'up', 'down'].map(f => (
            <button
              key={f}
              onClick={() => { setFilter(f); fetchFeedback(1, f); }}
              className="px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              style={{
                background: filter === f ? colors.accent : isDark ? 'rgba(30,41,59,0.5)' : '#f1f5f9',
                color: filter === f ? '#fff' : colors.textMuted,
              }}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f === 'up' ? 'Positive' : 'Negative'}
            </button>
          ))}
        </div>

        {/* Feedback List */}
        <div className="rounded-2xl overflow-hidden" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-16" style={{ color: colors.textMuted }}>No feedback yet</div>
          ) : (
            <div className="divide-y" style={{ borderColor: colors.cardBorder }}>
              {feedback.map((f, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3" data-testid={`feedback-row-${i}`}>
                  {f.rating === 'up' ? <ThumbsUp className="h-4 w-4 text-green-400 shrink-0" /> : <ThumbsDown className="h-4 w-4 text-red-400 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-xs truncate" style={{ color: colors.textMuted }}>Message: {f.message_id}</p>
                    <p className="text-[11px]" style={{ color: colors.textMuted, opacity: 0.6 }}>{new Date(f.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchFeedback(page - 1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm" style={{ color: colors.textMuted }}>Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => fetchFeedback(page + 1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function StatBox({ label, value, icon: Icon, color, colors, isDark }) {
  return (
    <div className="rounded-xl p-4" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4" style={{ color }} />
        <span className="text-xs font-medium" style={{ color: colors.textMuted }}>{label}</span>
      </div>
      <p className="text-2xl font-bold" style={{ color: colors.text }}>{value}</p>
    </div>
  );
}
